import { z } from 'zod';
/**
 * Schema for the property extraction response from OpenAI.
 * The LLM extracts either a street line (number + street) or a property/community name, plus optional unit.
 * City, region, postal, and country are omitted — fuzzy matching uses CRM data.
 * extractedAddress is null for unit-only mentions (no address or property name in transcript).
 */
export const PropertyExtractionResponseSchema = z.object({
    propertyMentions: z.array(z.object({
        extractedAddress: z
            .string()
            .nullable()
            .describe('Either (1) street line only: building number plus street name (directionals and suffix like North, Street, Avenue allowed), (2) property/community name as spoken (e.g. "Hillside Apartments", "Maple Creek Apartments"), or (3) a property code — a letter+digit token with no space (e.g. "retal4019", "z3720 Hubby Ave") — reproduced EXACTLY as written, never split, reordered, or reshaped into a street line. Never append city, state, province, postal code, or country. null when only a unit is mentioned with no address or property name.'),
        unit: z
            .string()
            .nullable()
            .describe('Unit or apartment number if mentioned, null otherwise'),
        confidence: z
            .number()
            .min(0)
            .max(1)
            .describe('Confidence that this is a genuine property mention'),
    })),
});
/**
 * Prompt template for extracting property address mentions from transcripts.
 * The LLM handles street-line or property-name normalization and STT artifact correction only —
 * it does not match against a property catalog.
 *
 * Required variables:
 *   content — the raw transcript or message text (may be multi-turn)
 */
export const propertyMatchingPrompt = {
    name: 'property-matching',
    version: '2.3.0',
    systemPrompt: 'You are a specialized assistant for extracting property identifiers from phone call transcripts: either a street line (number + street) or a property/community name (e.g. Hillside Apartments). Never include city or region.',
    userPrompt: `Extract any property addresses or property names mentioned in the following call transcript. Focus on what the caller says, not the agent.

{{content}}

Rules:
1. Only extract numbers that are being used as a building or street address. Numbers that refer to time ("12pm", "3 o'clock"), duration ("3 weeks", "two days"), quantities ("two units available"), phone numbers, or any other non-address context must be ignored entirely.
2. A valid mention is either (a) a street name paired with a building number, (b) a recognizable property or community name (e.g. "Hillside Apartments", "the Roosevelt building", "Maple Creek"), or (c) a property code — a single token that glues letters and digits together with no space (e.g. "retal4019", "smith2121", "z3720"), which properties are often named by. Isolated numbers or bare street names alone are not enough.
3. Normalize speech-to-text artifacts: convert number words to digits ("one two three" → "123", "twenty third floor" → "23rd floor", "four bee" → "4B"), expand abbreviations ("blvd" → "Boulevard", "st" → "Street", "ave" → "Avenue"), fix obvious transcription errors. This normalization does NOT apply to property-code tokens (rule 2c) — never split a code into separate number and word parts, never reorder its characters, never insert spaces, and never drop leading letters (e.g. "z3720" must NOT become "3720").
4. For extractedAddress, use ONLY one of: (i) the street line — building number plus street name, including directionals that are part of the street (e.g. "North" in "Main Street North"); (ii) the property/community name as spoken (e.g. "Hillside Apartments"), when the caller identifies the place by name rather than by street; or (iii) a property code (rule 2c) reproduced EXACTLY as written, including any trailing street ("z3720 Hubby Ave" stays "z3720 Hubby Ave"; "retal4019" stays "retal4019"). Do NOT reshape a code into a street line (e.g. "retal4100" must NOT become "4100 Retal Street"). In every case, do NOT append or include city, town, state, province, territory, postal code, ZIP, or country — even if the caller said them.
5. If a unit or apartment number is mentioned (e.g. "unit 4B", "apartamento 233", "apt 3"), put it ONLY in the unit field, never in extractedAddress.
6. If the caller refers to a property implicitly ("my place", "the building"), include it only if the specific address or name was established earlier in the transcript.
7. If multiple distinct properties or addresses are mentioned, return each as a separate entry in the array.
8. If no property address or name is clearly mentioned, return an empty array. Messages that are pure pleasantries, acknowledgments, or small talk with no property ("thank you", "you as well", "appreciate it", "sounds good", "ok") contain NO property — return an empty array for them.
8a. The quoted example values throughout these rules (e.g. "123 Main Street", "Hillside Apartments", "Maple Creek", "4B", "the Roosevelt building") are illustrations of FORMAT only. NEVER emit any of them unless the caller's message actually contains that value. Extract only what is literally present in the message above — if you find yourself about to output an example drawn from these instructions rather than from the message, return an empty array instead.
9. If the caller mentions only a unit number with no property address or name anywhere in the transcript (e.g. "I'm in unit E", "calling from apt 4B", "I am from unit five"), set extractedAddress to JSON null — never an empty string — and put the unit identifier in the unit field. Do not invent an address.

Return your response as JSON.`,
    variables: ['content'],
    variableSchema: z.object({
        content: z.string().describe('Transcript / Message'),
    }),
    responseSchema: PropertyExtractionResponseSchema,
    evalFields: [
        // Empty strings are filtered along with nulls: the model occasionally emits
        // "" for "no value", and all consumers treat the fields by truthiness.
        {
            key: 'extractedAddress',
            type: 'text[]',
            extract: (r) => r.propertyMentions
                .map(m => m.extractedAddress)
                .filter((a) => a != null && a.trim() !== ''),
        },
        {
            key: 'unit',
            type: 'text[]',
            extract: (r) => r.propertyMentions
                .map(m => m.unit)
                .filter((u) => u != null && u.trim() !== ''),
        },
    ],
    metadata: {
        description: 'Extracts street lines or property/community names from conversation transcripts (no city or region)',
        model: 'gpt-4o-mini',
        temperature: 0.1,
        maxTokens: 500,
        responseFormat: 'json_object',
        schema: 'PropertyExtractionResponseSchema',
    },
};
//# sourceMappingURL=property-matching.js.map